import {
  Analytics,
  logEvent as firebaseLogEvent,
  getAnalytics,
  isSupported as isAnalyticsSupported,
  setAnalyticsCollectionEnabled,
} from 'firebase/analytics'
import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app'

export interface FirebaseTrackerConfig {
  options: FirebaseOptions
  enableDebugMode?: boolean
}

let firebaseApp: FirebaseApp | null = null
let firebaseAnalytics: Analytics | null = null

export const initializeFirebaseTracker = async ({
  options,
  enableDebugMode = false,
}: FirebaseTrackerConfig) => {
  console.log('====================== Firebase Initialization =======================')
  console.log('Initializing Firebase tracker...')

  try {
    if (!options.appId || !options.projectId || !options.apiKey) {
      console.warn('Firebase configuration incomplete, skipping initialization')
      return
    }

    if (!firebaseApp) {
      firebaseApp = initializeApp(options)
    }

    const isSupported = await isAnalyticsSupported().catch(() => false)
    if (!isSupported) {
      console.warn('Firebase Analytics is not supported in this browser')
      return
    }

    firebaseAnalytics = getAnalytics(firebaseApp)
    setAnalyticsCollectionEnabled(firebaseAnalytics, true)

    // Enable debug mode if requested
    if (enableDebugMode) {
      await enableFirebaseDebugMode()
    }

    console.log(
      'Firebase tracker initialized successfully',
      enableDebugMode ? '(Debug mode enabled)' : '',
    )
  } catch (error) {
    console.error('Failed to initialize Firebase tracker:', error)
  }
}

/**
 * Enable Firebase Analytics debug mode
 * This allows you to see events in real-time in the Firebase Console DebugView
 */
export const enableFirebaseDebugMode = async () => {
  if (typeof window === 'undefined') {
    console.warn('Debug mode can only be enabled in browser environment')
    return
  }

  try {
    // Method 1: Set debug cookie (works in Chrome/Edge)
    document.cookie = 'firebase-analytics-debug=true; path=/; max-age=3600'

    // Method 2: Set localStorage flag
    localStorage.setItem('firebase-analytics-debug', 'true')

    // Method 3: Add debug parameter to URL (if not already present)
    const url = new URL(window.location.href)
    if (!url.searchParams.has('firebase-debug')) {
      url.searchParams.set('firebase-debug', 'true')
      // Note: We don't automatically navigate, but log the URL
      console.log('To enable debug mode via URL, navigate to:', url.toString())
    }

    console.log('✅ Firebase Debug Mode enabled!')
    console.log(
      '📊 View events in real-time at: https://console.firebase.google.com/project/YOUR_PROJECT_ID/analytics/debugview',
    )
    console.log('💡 Debug mode will be active for 1 hour (or until you clear cookies)')
  } catch (error) {
    console.error('Failed to enable Firebase debug mode:', error)
  }
}

/**
 * Disable Firebase Analytics debug mode
 */
export const disableFirebaseDebugMode = () => {
  if (typeof window === 'undefined') return

  try {
    document.cookie = 'firebase-analytics-debug=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
    localStorage.removeItem('firebase-analytics-debug')
    console.log('Firebase Debug Mode disabled')
  } catch (error) {
    console.error('Failed to disable Firebase debug mode:', error)
  }
}

export const trackFirebaseEvent = async (
  eventName: string,
  eventProperties: Record<string, unknown> = {},
) => {
  if (!firebaseAnalytics) {
    console.warn('Firebase Analytics is not initialized')
    return
  }

  const firebaseEventName = `gf_${eventName}`

  firebaseLogEvent(firebaseAnalytics, firebaseEventName, eventProperties)
  console.log('Tracking firebase event:', firebaseEventName, eventProperties)
}
